import bowser from "bowser";
import {
  getSelectorsByUserAgent,
  mobileModel,
  mobileVendor,
  osName,
} from "react-device-detect";
import { DeviceType } from "@onmo/graphql/graphql";
import type { OSInfo } from "../types/eonStats";

interface NativeDeviceBridge {
  getDeviceMake?: () => string;
  getDeviceModel?: () => string;
  getDeviceResolution?: () => number;
  getDeviceOsVersion?: () => string;
  getDeviceThermalState?: () => string;
  /** JSON [{name, types, hardware}] - H.264/H.265/AV1 decoders the platform declares. */
  getDecoderList?: () => string;
}

declare global {
  interface Window {
    DeviceBridge?: NativeDeviceBridge;
  }
}

interface TizenProductInfo {
  getRealModel?: () => string;
  is8KPanelSupported?: () => boolean;
  isUdPanelSupported?: () => boolean;
}

interface WebosDeviceInfo {
  modelName?: string;
  screenHeight?: number;
  sdkVersion?: string; // webOS.deviceInfo
  platformVersion?: string; // PalmSystem.deviceInfo
}

export interface DeviceInfoPayload<T = string> {
  make: string;
  model: string;
  os: string;
  resolution: number;
  type: T;
}

// ----- Platform detection TV (Samsung, android, LG) -----
function getTizenProductInfo(): TizenProductInfo | undefined {
  const w = window as unknown as {
    webapis?: { productinfo?: TizenProductInfo };
  };
  return w.webapis?.productinfo;
}
function getWebosDeviceInfoFn():
  | ((cb: (info: WebosDeviceInfo) => void) => void)
  | undefined {
  const w = window as unknown as {
    webOS?: { deviceInfo?: (cb: (info: WebosDeviceInfo) => void) => void };
  };
  return w.webOS?.deviceInfo;
}

function getPalmSystemInfo(): WebosDeviceInfo | undefined {
  const palm = (window as unknown as { PalmSystem?: { deviceInfo?: string } })
    .PalmSystem;
  if (!palm?.deviceInfo) return undefined;
  try {
    return JSON.parse(palm.deviceInfo) as WebosDeviceInfo;
  } catch {
    return undefined;
  }
}

function getPalmSystemModel(): string | undefined {
  return getPalmSystemInfo()?.modelName;
}

function getWebosVersion(): string {
  return (
    webosInfoCache?.sdkVersion || getPalmSystemInfo()?.platformVersion || ""
  );
}

function getTizenVersion(): string {
  let capability: unknown;
  try {
    capability = (
      window as unknown as {
        tizen?: { systeminfo?: { getCapability?: (key: string) => unknown } };
      }
    ).tizen?.systeminfo?.getCapability?.(
      "http://tizen.org/feature/platform.version",
    );
  } catch {
    capability = undefined;
  }
  if (typeof capability === "string" && capability) return capability;
  return /Tizen (\d+\.\d+)/.exec(navigator.userAgent)?.[1] ?? "";
}

export const isTizen = (): boolean => getTizenProductInfo() !== undefined;
const isWebos = (): boolean => getWebosDeviceInfoFn() !== undefined;
// -------------------------

// ---- all function to get device information ----
const UNAVAILABLE = "";

// webOS deviceInfo is async (callback); prime + cache at module load
let webosInfoCache: WebosDeviceInfo | null = null;
try {
  getWebosDeviceInfoFn()?.((info) => {
    webosInfoCache = info;
  });
} catch {
  webosInfoCache = null;
}

interface UaDataHighEntropy {
  model?: string;
  platformVersion?: string;
  architecture?: string; // "x86" | "arm" — blank on Android by design
  bitness?: string; // "64" | "32"
}
let uaDataCache: UaDataHighEntropy | undefined;
function primeUserAgentData(): Promise<void> {
  try {
    const uaData = (
      navigator as Navigator & {
        userAgentData?: {
          getHighEntropyValues?: (
            hints: string[],
          ) => Promise<UaDataHighEntropy>;
        };
      }
    ).userAgentData;
    const pending = uaData?.getHighEntropyValues?.([
      "model",
      "platformVersion",
      "architecture",
      "bitness",
    ]);
    if (!pending) return Promise.resolve();
    return pending
      .then((values) => {
        uaDataCache = values;
      })
      .catch(() => {
        uaDataCache = undefined;
      });
  } catch {
    uaDataCache = undefined;
    return Promise.resolve();
  }
}

export const deviceInfoReady: Promise<void> = primeUserAgentData();

// CPU/thermal pressure (Compute Pressure API — Chromium only): async; prime + cache
let thermalStateCache: string | undefined;
function primeThermalState(): void {
  const PressureObserverCtor = (
    window as unknown as {
      PressureObserver?: new (
        callback: (records: { state?: string }[]) => void,
      ) => { observe: (source: string) => Promise<void> };
    }
  ).PressureObserver;
  if (typeof PressureObserverCtor !== "function") return;
  try {
    const observer = new PressureObserverCtor((records) => {
      const latest = records[records.length - 1];
      if (latest?.state) thermalStateCache = latest.state;
    });
    observer.observe("cpu").catch(() => {
      thermalStateCache = undefined;
    });
  } catch {
    thermalStateCache = undefined;
  }
}
primeThermalState();

/** Android bridge + web Chromium (Compute Pressure); Tizen/webOS/iOS → none. */
export function getThermalState(): string | undefined {
  return (
    window.DeviceBridge?.getDeviceThermalState?.() ||
    thermalStateCache ||
    undefined
  );
}

// make and model
export function getMakeAndModel(): { make: string; model: string } {
  const bridgeMake = window.DeviceBridge?.getDeviceMake?.() || UNAVAILABLE;
  const bridgeModel = window.DeviceBridge?.getDeviceModel?.() || UNAVAILABLE;
  if (bridgeMake || bridgeModel) {
    return { make: bridgeMake, model: bridgeModel };
  }

  if (isTizen()) {
    try {
      return {
        make: "Samsung",
        model: getTizenProductInfo()?.getRealModel?.() || UNAVAILABLE,
      };
    } catch {
      return { make: "Samsung", model: UNAVAILABLE };
    }
  }
  if (isWebos()) {
    return {
      make: "LG",
      model: webosInfoCache?.modelName || getPalmSystemModel() || UNAVAILABLE,
    };
  }

  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) {
    const isIpad =
      /iPad/i.test(ua) ||
      (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const model = /iPod/i.test(ua)
      ? "iPod"
      : /iPhone/i.test(ua)
      ? "iPhone"
      : isIpad
      ? "iPad"
      : "Mac";
    return { make: "Apple", model };
  }

  if (mobileVendor && mobileVendor !== "none") {
    return {
      make: mobileVendor,
      model: mobileModel && mobileModel !== "none" ? mobileModel : UNAVAILABLE,
    };
  }

  const uaDataModel = uaDataCache?.model;
  if (uaDataModel) {
    const { mobileVendor: vendor } = getSelectorsByUserAgent(
      `Mozilla/5.0 (Linux; Android 13; ${uaDataModel}) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36`,
    );
    return {
      make: vendor && vendor !== "none" ? vendor : UNAVAILABLE,
      model: uaDataModel,
    };
  }

  return { make: UNAVAILABLE, model: UNAVAILABLE };
}

function getLandscapeScreenHeight(): number {
  const { width, height } = window.screen;
  const shortEdge = Math.min(width || 0, height || 0) || height || width || 0;
  return Math.round(shortEdge * (window.devicePixelRatio || 1));
}

function getTizenResolution(): number | null {
  try {
    const productinfo = getTizenProductInfo();
    if (!productinfo) return null;
    if (productinfo.is8KPanelSupported?.()) return 4320;
    if (productinfo.isUdPanelSupported?.()) return 2160;
    return 1080;
  } catch {
    return null;
  }
}
export function getResolution(): number {
  if (window.DeviceBridge?.getDeviceResolution) {
    const res = window.DeviceBridge.getDeviceResolution();
    if (res) return res;
  }
  if (isWebos() && webosInfoCache?.screenHeight) {
    return webosInfoCache.screenHeight;
  }
  if (isTizen()) {
    const tizenHeight = getTizenResolution();
    if (tizenHeight) return tizenHeight;
  }
  return getLandscapeScreenHeight();
}

export function getCpuCores(): number | undefined {
  return navigator.hardwareConcurrency || undefined;
}

export function getRamGB(): number | undefined {
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  return typeof mem === "number" ? mem : undefined;
}

// ----- CPU architecture -----
export type CpuArchitecture = "arm64" | "arm" | "x86_64" | "x86";

/**
 * Folds every dialect into the four canonical values: UA-CH pairs
 * ("arm" + "64"), Android ABIs ("arm64-v8a"), iOS `hw.machine`, and the
 * `armv8l`/`x86_64` tokens UA strings leak. ARMv8/v9 counts as 64-bit — the
 * field describes the chip, not the bitness of the process reading it.
 */
export function normalizeCpuArchitecture(
  raw: string | undefined,
  bitness?: string,
): CpuArchitecture | undefined {
  const value = raw?.trim().toLowerCase();
  if (!value) return undefined;
  const is64 = bitness === "64" || /64|v[89]/.test(value);
  if (/arm|aarch/.test(value)) return is64 ? "arm64" : "arm";
  if (/x86|x64|win64|wow64|amd64|i[3-6]86/.test(value)) {
    return is64 ? "x86_64" : "x86";
  }
  return undefined;
}

/** Route 1 — UA Client Hints. Chromium desktop only; Android reports "". */
function getUaChCpuArchitecture(): CpuArchitecture | undefined {
  return normalizeCpuArchitecture(
    uaDataCache?.architecture,
    uaDataCache?.bitness,
  );
}

/**
 * Route 2 — arch tokens still present in the UA/platform string: desktop Linux
 * and Windows, ChromeOS, older TV browsers, and Android's `Linux armv8l`
 * platform. macOS is deliberately not matched — it says `MacIntel` on Apple
 * Silicon too, so route 3 has to settle it.
 */
const UA_ARCH_TOKEN =
  /aarch64|arm64|armv\d+\w*|\barm\b|x86_64|amd64|wow64|win64|x64|i[3-6]86|x86/i;

function getUaStringCpuArchitecture(): CpuArchitecture | undefined {
  const ua = `${navigator.userAgent} ${navigator.platform ?? ""}`;
  return normalizeCpuArchitecture(UA_ARCH_TOKEN.exec(ua)?.[0]);
}

/** Tegra is NVIDIA-branded but ARM, so the ARM test has to run first. */
const ARM_GPU =
  /\bmali\b|adreno|powervr|xclipse|immortalis|tegra|apple\s*(gpu|[am]\d)/i;
const X86_GPU = /intel|\bamd\b|radeon|nvidia|geforce|llvmpipe|swiftshader/i;

/** Route 3 — GPU renderer. The only tell for Apple Silicon vs Intel in Safari. */
function getGpuCpuArchitecture(
  renderer: string | undefined,
): CpuArchitecture | undefined {
  if (!renderer) return undefined;
  if (ARM_GPU.test(renderer)) return "arm64";
  if (X86_GPU.test(renderer)) return "x86_64";
  return undefined;
}

/**
 * Route 4 — per-platform fact, for runtimes that expose nothing at all.
 * Samsung/LG panels are ARM across the fleet but publish no bitness, so they
 * stay at the family; every supported iOS device and effectively every Android
 * device in the fleet is arm64.
 */
function getPlatformCpuArchitecture(): CpuArchitecture | undefined {
  if (isTizen()) return "arm";
  if (isWebos()) return "arm";
  const ua = navigator.userAgent;
  const isIpadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/iphone|ipad|ipod/i.test(ua) || isIpadOS) return "arm64";
  if (/android/i.test(ua)) return "arm64";
  return undefined;
}

export function getCpuArchitecture(
  gpuRenderer?: string,
): CpuArchitecture | undefined {
  return (
    getUaChCpuArchitecture() ??
    getUaStringCpuArchitecture() ??
    getGpuCpuArchitecture(gpuRenderer) ??
    getPlatformCpuArchitecture()
  );
}

function getOsInfo(): OSInfo {
  if (isTizen()) return { name: "Tizen", version: getTizenVersion() };
  if (isWebos()) return { name: "webOS", version: getWebosVersion() };
  const ua = window.navigator.userAgent;
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return { name: "iPadOS", version: /Version\/(\d+)/.exec(ua)?.[1] ?? "" };
  }
  const parser = bowser.getParser(ua);
  const rddOsName = osName && osName !== "none" ? osName : "";
  const rawName = parser.getOSName() || rddOsName || "";
  const name = rawName === "WebOS" ? "webOS" : rawName;

  if (name === "webOS") return { name, version: getWebosVersion() };
  if (name === "Tizen") return { name, version: getTizenVersion() };
  return {
    name,
    version:
      window.DeviceBridge?.getDeviceOsVersion?.() ||
      (name === "Android" && uaDataCache?.platformVersion) ||
      parser.getOSVersion() ||
      "",
  };
}
// ----- Device type -----
const isAndroidTv = (): boolean =>
  (
    window as unknown as { AndroidBridge?: { isTV?: () => boolean } }
  ).AndroidBridge?.isTV?.() === true;

/** Capacitor puts itself on `window` in both the web and the native shell. */
const isNativeApp = (): boolean =>
  (
    window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
  ).Capacitor?.isNativePlatform?.() === true;

/**
 * Backend device type resolved from the runtime alone — platform objects
 * (Tizen `webapis`, webOS `deviceInfo`, the Android bridge) first, user agent
 * after — so callers don't have to thread it down from the app.
 */
export function getDeviceType(): DeviceType {
  const ua = navigator.userAgent;

  if (isTizen() || /tizen/i.test(ua)) return DeviceType.Tv;
  if (isWebos() || /web[o0]s/i.test(ua)) return DeviceType.Tv;

  const native = isNativeApp();

  // Chromebooks report Linux + touch, so they have to be settled before the
  // Android TV and tablet branches below.
  if (/\bCrOS\b/i.test(ua)) {
    return native ? DeviceType.DesktopNative : DeviceType.DesktopWeb;
  }
  if (
    isAndroidTv() ||
    /\bAFT[A-Z0-9]|BRAVIA|Android ?TV|Google ?TV|CrKey|MiBOX|MiTV/i.test(ua)
  ) {
    return DeviceType.Tv;
  }

  // iPadOS 13+ sends a desktop Macintosh UA; touch points are the only tell.
  const isIpadOS = /macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  if (/tablet|ipad|playbook|silk/i.test(ua) || isIpadOS) {
    return native ? DeviceType.TabletNative : DeviceType.TabletWeb;
  }

  if (/mobile/i.test(ua)) {
    return native ? DeviceType.MobileNative : DeviceType.MobileWeb;
  }

  // Android with no "Mobile" token is a tablet (Chrome >= 110 reduced UA).
  if (/Android/i.test(ua)) {
    return native ? DeviceType.TabletNative : DeviceType.TabletWeb;
  }

  return native ? DeviceType.DesktopNative : DeviceType.DesktopWeb;
}

export function buildDeviceInfo<T>(type: T): DeviceInfoPayload<T> {
  const { make, model } = getMakeAndModel();
  return {
    make,
    model,
    os: getOsInfo().name,
    resolution: getResolution(),
    type,
  };
}

// -------------------------
